"use client";

import { useState } from "react";
import {
  ArrowRight,
  Check,
  FileSearch,
  Link2,
  LockKeyhole,
  Plus,
} from "lucide-react";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import type { SeoAnalysisCopy } from "./seo-specialist-copy";
import styles from "./seo-specialist.module.scss";

export function CcSeoAuditExample({
  copy,
}: {
  copy: SeoAnalysisCopy["example"];
}) {
  const [active, setActive] = useState(0);
  const [fixed, setFixed] = useState(false);
  const issue = copy.issues[active];
  return (
    <section
      className={`cc-section ${styles.exampleSection}`}
      id="seo-example"
      aria-labelledby="seo-example-title"
      data-cc-nav="light"
      data-cc-surface="light"
    >
      <div className={styles.shell}>
        <div className={styles.sectionHeader}>
          <CcSanjayaEyebrow>{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="seo-example-title">{copy.title}</h2>
          <p>{copy.text}</p>
        </div>
        <div className={styles.auditLayout}>
          <div className={styles.auditPaper}>
            <div className={styles.paperBar}>
              <span>
                <i />
                <i />
                <i />
              </span>
              <small>
                <LockKeyhole size={12} />
                {copy.domain}
              </small>
              <Plus size={15} />
            </div>
            <div className={styles.paperPage}>
              <span className={styles.paperBrand}>
                <span aria-hidden="true">e.</span>
                {copy.label}
              </span>
              <div
                className={styles.codeFlag}
                data-highlight={active === 0}
                data-fixed={active === 0 && fixed}
              >
                <code>
                  &lt;meta name=&quot;robots&quot;
                  <br />
                  content=&quot;
                  {active === 0 && fixed ? "index, follow" : copy.issues[0].tag}
                  &quot;&gt;
                </code>
              </div>
              <h3
                className={styles.paperTitle}
                data-highlight={active === 1}
                data-fixed={active === 1 && fixed}
              >
                {active === 1 && fixed ? copy.pageTitle : copy.originalTitle}
              </h3>
              <p>{copy.pageText}</p>
              <div className={styles.paperAbstract} aria-hidden="true">
                <div />
                <div />
                <div />
                <span>
                  <FileSearch size={48} strokeWidth={1} />
                </span>
              </div>
              <div
                className={styles.linkFlag}
                data-highlight={active === 2}
                data-fixed={active === 2 && fixed}
              >
                <Link2 size={17} />
                <span>
                  {active === 2 && fixed ? copy.linked : copy.issues[2].tag}
                </span>
              </div>
            </div>
            <span className={styles.paperStamp} aria-hidden="true">
              {copy.stamp.top}
              <br />
              {copy.stamp.bottom}
            </span>
          </div>
          <div className={styles.auditFindings}>
            <div className={styles.findingButtons}>
              {copy.issues.map((item, index) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => {
                    setActive(index);
                    setFixed(false);
                  }}
                  aria-pressed={active === index}
                  aria-controls="seo-finding-detail"
                >
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <strong>{item.title}</strong>
                  <ArrowRight size={18} aria-hidden="true" />
                </button>
              ))}
            </div>
            <div
              className={styles.findingDetail}
              id="seo-finding-detail"
              aria-live="polite"
            >
              <small className={styles.priority}>{issue.priority}</small>
              <h3>{copy.impactLabel}</h3>
              <p>{issue.impact}</p>
              <div className={styles.actionDetail}>
                <Check size={18} aria-hidden="true" />
                <div>
                  <h3>{copy.actionLabel}</h3>
                  <p>{issue.action}</p>
                </div>
              </div>
            </div>
            <button
              className={styles.showFix}
              type="button"
              aria-pressed={fixed}
              onClick={() => setFixed(!fixed)}
            >
              {fixed ? copy.showFinding : copy.showFix}
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
        <p className={styles.exampleNote}>{copy.sampleNote}</p>
      </div>
    </section>
  );
}
